import { VEHICLE_POLICY_FIELDS } from './vehiclePolicyConfig';

/** @typedef {'inh' | 'cust' | 'lock' | null} FieldBadge */

const SPECIFIC_TOOLTIPS = {
    __presetName__: {
        title: 'Vehicle preset name',
        desc: 'A name for this preset (e.g. fleet segment or model line). Used when assigning the preset to assets.',
    },
    __newVehicleType__: {
        title: 'New body type',
        desc: 'Adds a body type under the selected vehicle class for every agency to use. Types cannot be renamed or deleted afterwards — only deactivated.',
    },
    __vehicleType__: {
        title: 'Vehicle type',
        desc: 'Body type for this preset (e.g. Pickup / 4x4), grouped under a vehicle class. Changing type loads trip & idle defaults for the new class; customized Motion/Trip values are kept until you confirm the replace.',
    },
    __vehicleClass__: {
        title: 'Vehicle class',
        desc: 'High-level class (Bike, Car SUV, Van Truck, Heavy Truck, Bus). Used to group body types and fall back trip/idle limits.',
    },
    __newVehicleClass__: {
        title: 'New vehicle class',
        desc: 'Adds a custom vehicle class for grouping body types. Prefer the default classes unless your fleet needs a new category.',
    },
    __description__: {
        title: 'Description',
        desc: 'Optional internal note for operators. Does not change device behaviour.',
    },
    __activePrice__: {
        title: 'Active price (resolved)',
        desc: 'Fuel or energy unit price resolved from the central registry for the selected fuel type and effective date.',
    },
    __vibrationGroup__: {
        title: 'Vibration sensitivity (X / Y / Z)',
        desc: 'Axis-specific G-force thresholds for vibration and harsh-event detection. Lower values are more sensitive.',
    },
    __fuelCalibrationMap__: {
        title: 'Fuel calibration map',
        desc: 'Hardware table mapping raw sensor percentage to litres. Maintained by administrators; not editable in this view.',
    },
    'Maximum Tank Capacity': {
        title: 'Tank capacity',
        desc: 'Maximum tank volume in litres. Used to convert level readings into volume for reporting and alerts.',
    },
    'Minimum Refill Detection': {
        title: 'Refill threshold',
        desc: 'Minimum volume increase required to register a refill event and stitch fuel accounting after a stop.',
    },
    'Theft/Drain Detection Alert': {
        title: 'Theft / drain threshold',
        desc: 'Minimum fuel drop while ignition is off before a theft or suspicious drain alert is raised.',
    },
    'Sensor Noise/Spike Filter (L)': {
        title: 'Spike filter',
        desc: 'Maximum single-step drop to ignore as sensor noise or slosh while the vehicle is moving.',
    },
    'Fuel Stabilization Wait Time (Sec)': {
        title: 'Slosh settle duration',
        desc: 'Wait time for the level to stabilise before confirming refill, drain, or theft states.',
    },
    'Allowed Stabilization Fluctuation (L)': {
        title: 'Slosh tolerance',
        desc: 'Allowed wobble in litres during the stabilisation window before the reading is considered stable.',
    },
    'Micro-refill (L)': {
        title: 'Micro-refill',
        desc: 'Minimum stationary top-up below the main refill threshold. Leave blank to keep detection off.',
    },
    'Offline gap cap (min)': {
        title: 'Offline gap cap',
        desc: 'Maximum minutes from each telemetry gap counted toward engine and idle duration.',
    },
    'Drain window (min)': {
        title: 'Drain window',
        desc: 'Sliding period used to distinguish sustained drain or theft from normal fuel burn.',
    },
    'Max burn rate (L/hr)': {
        title: 'Max burn rate',
        desc: 'Fastest physically plausible running fuel burn; faster sustained drops are classified as drains.',
    },
    'Excessive Idling/Standby Timeout (Sec)': {
        title: 'Excessive idle limit',
        desc: 'Idle beyond this counts as excessive. Comes from the vehicle-class preset.',
    },
    'Minimum Speed to Register Movement (km/h)': {
        title: 'Moving speed threshold',
        desc: 'At or below this the vehicle counts as stationary for idle detection.',
    },
    'Enable Excusable Idling': {
        title: 'Excusable idling',
        desc: 'When off, all excessive idle stays 450 regardless of temperature.',
    },
    'Max Excusable Idle Duration (Sec)': {
        title: 'Excusable cap',
        desc: 'Per-day ceiling on excused idle time. Overflow reverts to 450.',
    },
    'Hot Threshold (°C)': {
        title: 'Hot threshold',
        desc: 'Ambient above this excuses excessive idle — climate control in heat is an occupational need.',
    },
    'Cold Threshold (°C)': {
        title: 'Cold threshold',
        desc: 'Ambient below this also excuses excessive idle.',
    },
    'Ambient Proxy': {
        title: 'Ambient proxy',
        desc: 'Only for exports with no ambient temperature column. HOURS assumes hot ambient inside the window below. Results are estimates, not evidence.',
    },
    'Proxy From (h)': {
        title: 'Proxy from',
        desc: 'Local hour the hot window opens when ambient proxy is set to daylight-hours estimate.',
    },
    'Proxy To (h)': {
        title: 'Proxy to',
        desc: 'Local hour the hot window closes when ambient proxy is set to daylight-hours estimate.',
    },
    'Stop-and-go Merge (Sec)': {
        title: 'Stop-and-go merge',
        desc: 'Two idle runs separated by less than this, with no speed above the creep limit, count as one episode.',
    },
    'Creep Limit (km/h)': {
        title: 'Creep limit',
        desc: 'Movement below this between two stops is crawling in traffic, not a journey leg.',
    },
    'Software Trip Start Speed (km/h)': {
        title: 'Trip start speed',
        desc: 'Speed required to officially open a journey.',
    },
    'Minimum Distance to Force Trip Start (km)': {
        title: 'Trip start distance',
        desc: 'Pending distance that can open a trip without reaching start speed.',
    },
    'Minimum Total Distance for Valid Trip (km)': {
        title: 'Minimum trip distance',
        desc: 'Shorter journeys are ghost trips, excluded from all metrics.',
    },
    'Ignition OFF Trip End Timeout (Sec)': {
        title: 'Ignition-off timeout',
        desc: 'How long ignition must stay off before the trip closes.',
    },
    'Ignition Source': {
        title: 'Ignition source',
        desc: 'Which signal decides whether the engine is running. Leave on As recorded unless the tracker was wired to the wrong input.',
    },
    'RPM Above Which Engine Is Running (rpm)': {
        title: 'RPM running threshold',
        desc: 'Anything above this counts as running. 0 suits most vehicles — a stopped engine publishes no RPM or a literal zero.',
    },
    'Voltage Above Which Engine Is Running (V)': {
        title: 'Voltage running threshold',
        desc: 'Set this above the resting bus voltage, not below it, so ignition does not stick ON.',
    },
    'Close Trips on Long Stops': {
        title: 'Close trips on long stops',
        desc: 'For vehicles that leave the engine running between jobs. A long stationary dwell closes the trip when ignition cycles cannot. Leave off when the vehicle shuts down between jobs.',
    },
    'Stop Length to Close (Sec)': {
        title: 'Stop length to close',
        desc: 'How long the vehicle must sit still before the trip is closed. The trip is stamped at the moment the stop began.',
    },
    'Stop Speed (km/h)': {
        title: 'Stop speed',
        desc: 'At or below this the vehicle counts as stopped. Keep it just above GPS jitter.',
    },
    'Stop Drift Allowance (m)': {
        title: 'Stop drift allowance',
        desc: 'How far the vehicle may wander during the stop and still count as parked.',
    },
    'Weak Battery Cranking Alert (Volts)': {
        title: 'Crank warning threshold',
        desc: 'Minimum voltage seen during cranking that triggers a weak-battery warning before outright failure.',
    },
    'Cranking Battery Failure Limit (Volts)': {
        title: 'Crank failure threshold',
        desc: 'Voltage below which the starter battery is treated as failed or unable to start the engine reliably.',
    },
    'Max Parked Parasitic Drain (V/hr)': {
        title: 'Parasitic drain limit',
        desc: 'Maximum allowed parked voltage decay rate before a parasitic drain diagnostic is raised.',
    },
};

const defaultDescForKey = (fieldKey) => (
    `Sets ${fieldKey.replace(/\s*\([^)]*\)\s*$/g, '').trim()} for this vehicle preset.`
);

/** @param {string} fieldKeyOrSpecial */
export const getFieldTooltip = (fieldKeyOrSpecial) => {
    if (SPECIFIC_TOOLTIPS[fieldKeyOrSpecial]) {
        return SPECIFIC_TOOLTIPS[fieldKeyOrSpecial];
    }
    return {
        title: fieldKeyOrSpecial,
        desc: defaultDescForKey(fieldKeyOrSpecial),
    };
};

/** Baseline policy values shown on “reset” for demo / typical override fields */
export const POLICY_BASELINE_VALUE = {
    'Minimum Refill Detection': '15',
    'Cranking Battery Failure Limit (Volts)': '20',
    'Receipt Calibration Multiplier': '1',
};

/** Initial badge per field (null = no badge). Mirrors typical inherited vs client override vs admin-locked patterns. */
export const INITIAL_FIELD_BADGE = VEHICLE_POLICY_FIELDS.reduce((acc, { fieldKey }) => {
    acc[fieldKey] = null;
    return acc;
}, {});

Object.assign(INITIAL_FIELD_BADGE, {
    'Maximum Tank Capacity': 'inh',
    'Minimum Refill Detection': 'cust',
    'Theft/Drain Detection Alert': 'inh',
    'Sensor Noise/Spike Filter (L)': 'inh',
    'Fuel Stabilization Wait Time (Sec)': 'inh',
    'Allowed Stabilization Fluctuation (L)': 'inh',
    'Weak Battery Cranking Alert (Volts)': 'inh',
    'Cranking Battery Failure Limit (Volts)': 'cust',
    'Alternator Charging Minimum (Volts)': 'inh',
    'Alternator Failure Wait Time (Sec)': 'inh',
    'Max Parked Parasitic Drain (V/hr)': 'inh',
    'Battery System Architecture': 'inh',
    'Receipt Calibration Multiplier': 'cust',
    'Overspeeding Wind Resistance Penalty (%)': 'inh',
});
